/**
 * Conflict-resolution helpers shared by the client sync engine and the
 * `sync-push` edge function. Kept dependency-free so the Deno edge runtime can
 * use a byte-aligned duplicate (`supabase/functions/_shared/conflict.ts`).
 *
 * Background: a master `update` carries a stale `base_version` when another
 * device already bumped the row server-side. `sync-push` then resolves it as
 * `lww_server` (server value wins). If the client's patch is *identical* to the
 * current server row (only `base_version` differs), the write is a pure no-op:
 * nothing actually conflicts, yet a `sync_conflict` event was still being
 * appended to the immutable event log on every sync tick — an infinite stream
 * of spurious events. `isNoOpMasterPatch` lets the server suppress that audit
 * event for no-op writes without changing accept/apply behaviour.
 */

/** Structural equality for JSON-serialisable values (null / primitive / array / object). */
export function deepEqualJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  const aArr = Array.isArray(a);
  const bArr = Array.isArray(b);
  if (aArr !== bArr) return false;

  if (aArr && bArr) {
    if (a.length !== b.length) return false;
    return a.every((x, i) => deepEqualJson(x, (b as unknown[])[i]));
  }

  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const aKeys = Object.keys(ao);
  const bKeys = Object.keys(bo);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    (k) => Object.prototype.hasOwnProperty.call(bo, k) && deepEqualJson(ao[k], bo[k]),
  );
}

/**
 * Fields the server owns/derives — differences here never count as a real
 * content change. `version` is the OCC counter the conflict is *about*;
 * timestamps are server-stamped.
 */
export const SERVER_MANAGED_MASTER_FIELDS: ReadonlySet<string> = new Set([
  'id',
  'version',
  'last_rev',
  'created_at',
  'updated_at',
  'archived_at',
]);

/**
 * True when applying `patch` to `current` would change nothing meaningful, i.e.
 * every non-server-managed key in the patch already deep-equals the same key on
 * the server row.
 *
 * Conservative by design: a patch key that is absent from the server row is
 * treated as a real change (returns false) — we never silently classify an
 * unknown/new column as a no-op.
 */
export function isNoOpMasterPatch(
  patch: Record<string, unknown>,
  current: Record<string, unknown>,
  serverManagedFields: ReadonlySet<string> = SERVER_MANAGED_MASTER_FIELDS,
): boolean {
  for (const [key, value] of Object.entries(patch)) {
    if (serverManagedFields.has(key)) continue;
    if (!Object.prototype.hasOwnProperty.call(current, key)) return false;
    if (!deepEqualJson(value, current[key])) return false;
  }
  return true;
}
